import { ReportTestItem } from './entities';
import { KPITileConfig } from '../types/kpi-types';

/**
 * Extracts a value from a ReportTestItem based on the field identifier.
 * Supports both standard fields (status, result, score, etc.) and custom fields.
 */
function extractValue(item: ReportTestItem, field: string): string | number | boolean | null {
  // Standard field mappings
  const standardFieldMap: Record<string, (item: ReportTestItem) => any> = {
    'status': (item) => item.status,
    'result': (item) => item.result,
    'rating': (item) => item.result,
    'score': (item) => item.score,
    'asset': (item) => item.asset_name,
    'building': (item) => item.building_description,
    'level': (item) => item.level_description,
    'area': (item) => item.area_description,
    'element': (item) => item.element_name,
    'element_id': (item) => item.element_id,
    'test_specification': (item) => item.testSpecification?.label,
    'test_suite': (item) => item.testSpecification?.testSuiteId,
  };

  // Check if it's a standard field
  if (standardFieldMap[field]) {
    return standardFieldMap[field](item);
  }

  // Check if it's a custom field (UUID format or in customDisplayValues)
  if (item.customDisplayValues && item.customDisplayValues[field] !== undefined) {
    return item.customDisplayValues[field];
  }

  // Fallback to custom object
  if (item.custom && item.custom[field] !== undefined) {
    return item.custom[field];
  }

  return null;
}

/**
 * Checks if a value is considered "valid" (not null, undefined, or empty string)
 */
function hasValue(value: any): boolean {
  return value !== null && value !== undefined && value !== '';
}

/**
 * Converts a value to a number, handling strings and null values
 */
function toNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  return isNaN(num) ? null : num;
}

/**
 * Calculates a KPI value from filtered data based on the tile configuration
 */
export function calculateKPI(data: ReportTestItem[], config: KPITileConfig): string | number {
  if (!data || data.length === 0) {
    return 0;
  }

  const values = data
    .map(item => extractValue(item, config.field))
    .filter(hasValue);

  if (values.length === 0) {
    return 0;
  }

  switch (config.operation) {
    case 'count':
      return values.length;

    case 'distinct_count': {
      const uniqueValues = new Set(values.map(v => String(v)));
      return uniqueValues.size;
    }

    case 'sum': {
      const numbers = values.map(toNumber).filter((n): n is number => n !== null);
      if (numbers.length === 0) return 0;
      return numbers.reduce((sum, n) => sum + n, 0);
    }

    case 'avg': {
      const numbers = values.map(toNumber).filter((n): n is number => n !== null);
      if (numbers.length === 0) return 0;
      const sum = numbers.reduce((sum, n) => sum + n, 0);
      return Number((sum / numbers.length).toFixed(2));
    }

    case 'min': {
      const numbers = values.map(toNumber).filter((n): n is number => n !== null);
      if (numbers.length === 0) return 0;
      return Math.min(...numbers);
    }

    case 'max': {
      const numbers = values.map(toNumber).filter((n): n is number => n !== null);
      if (numbers.length === 0) return 0;
      return Math.max(...numbers);
    }

    default:
      return 0;
  }
}

